/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { usePortalContext } from '@cloai/uikit';
import { usePathname, useSearchParams } from 'next/navigation';
import { PropsWithChildren, useEffect } from 'react';
import { useCookies } from 'react-cookie';

import { PORTALS } from '@/.constants/portals';
import {
  useProfileContext,
  useGTMContext,
  useStyleContext,
  TPortalSeeAllMode,
} from '@/src/providers';

const DEFAULT_LANGUAGE = 'en';
const GENERATE_PHOTO_BY_PHOTO_TYPE = 'generatePhotoByPhoto';

// GTM event mapping
const GTM_EVENT_MAP: Record<string, string> = {
  '/': 'open_menu',
  '/inpaint': 'open_inpaint',
};

const DEFAULT_GTM_EVENT = 'open_menu';

export default function Layout({ children }: PropsWithChildren) {
  const profile = useProfileContext();
  const sendGTM = useGTMContext();

  const { openPortal } = usePortalContext();
  const {
    setPrevPortal,
    setPortalSeeAllMode,
    changeActiveCategory,
    styleCategories,
    effectsCategories,
  } = useStyleContext();

  const searchParams = useSearchParams();
  const defaultOpenType = searchParams.get('defaultOpenType');
  const defaultOpenStyleCategoryId = searchParams.get(
    'defaultOpenStyleCategoryId',
  );

  const [, setCookie] = useCookies(['lang']);
  const pathname = usePathname();

  // Update language cookie when profile changes
  useEffect(() => {
    if (profile?.lang) {
      setCookie('lang', profile?.lang);
    } else {
      setCookie('lang', DEFAULT_LANGUAGE);
    }
  }, [profile?.lang]);

  // Send GTM event based on pathname
  useEffect(() => {
    if (sendGTM) {
      const eventName = GTM_EVENT_MAP[pathname] || DEFAULT_GTM_EVENT;
      sendGTM({
        event: eventName,
      });
    }
  }, [pathname]);

  useEffect(() => {
    if (!defaultOpenType) return;

    setPrevPortal('main');

    if (defaultOpenType === GENERATE_PHOTO_BY_PHOTO_TYPE) {
      openPortal(PORTALS.GeneratePhotoByPhoto);
      return;
    }

    if (defaultOpenStyleCategoryId) {
      const styleCategory = styleCategories.find(
        (category) => category.id === defaultOpenStyleCategoryId,
      );

      const effectsCategory = effectsCategories.find(
        (category) => category.id === defaultOpenStyleCategoryId,
      );

      if (effectsCategory && defaultOpenType === 'effectsCategories') {
        changeActiveCategory(defaultOpenStyleCategoryId, 'effects');
      } else if (styleCategory) {
        changeActiveCategory(defaultOpenStyleCategoryId, 'style');
      } else {
        changeActiveCategory(defaultOpenStyleCategoryId, 'video');
      }
    }

    setPortalSeeAllMode(defaultOpenType as TPortalSeeAllMode);
    openPortal(PORTALS.SeeAllImages);
  }, [defaultOpenStyleCategoryId, defaultOpenType]);

  return children;
}
